import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { type Location } from "@src/appV2/redesign/utils/routes";
import { startTransition } from "react";
import { useHistory } from "react-router-dom";

import { Badge } from "../../../../components/Badge";
import { Icon, type IconType } from "../../../../components/Icon";
import { Text } from "../../../../components/Text";
import { applyPlatformAwareInteractionStyles } from "../../../../utils/applyPlatformAwareInteractionStyles";

interface NavigationMenuItemTabletProps {
  children?: React.ReactNode;
  iconType: IconType;
  isSelected: boolean;
  routeTo?: Location;
  onClick?: () => void;
  onPreload?: () => void | Promise<void>;
  badgeCount?: number;
}

export function NavigationMenuItemTablet(props: NavigationMenuItemTabletProps) {
  const { children, iconType, isSelected, routeTo, onClick, onPreload, badgeCount } = props;

  const history = useHistory();

  const runPreloadSafely = () => {
    if (!onPreload) {
      return;
    }

    try {
      void Promise.resolve(onPreload()).catch(() => {
        // best-effort preload; never block navigation
      });
    } catch {
      // best-effort preload; never block navigation
    }
  };

  return (
    <Box
      {...((badgeCount ?? 0) > 0 && { "data-nav-badge-count": String(badgeCount) })}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        paddingY: 2,
        paddingX: 2,
        height: "54px",
        flexShrink: 0,
        gap: 2,
        borderRadius: theme.borderRadius?.medium,
        cursor: "pointer",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        transition: theme.transitions.create(["background-color"], {
          duration: theme.transitions.duration.short,
        }),

        color: isSelected
          ? theme.palette.navbar?.itemContentSelected
          : theme.palette.navbar?.itemContent,

        backgroundColor: isSelected ? theme.palette.navbar?.itemSelectedBackground : "transparent",

        ...applyPlatformAwareInteractionStyles({
          hoverStyle: {
            backgroundColor: theme.palette.navbar?.itemSelectedBackground,
            color: theme.palette.navbar?.itemContentSelected,
          },

          defaultStyle: {
            backgroundColor: isSelected
              ? theme.palette.navbar?.itemSelectedBackground
              : "transparent",

            color: isSelected
              ? theme.palette.navbar?.itemContentSelected
              : theme.palette.navbar?.itemContent,
          },
        }),
      })}
      onMouseEnter={runPreloadSafely}
      onFocus={runPreloadSafely}
      onPointerDown={runPreloadSafely}
      onClick={() => {
        runPreloadSafely();

        startTransition(() => {
          if (isDefined(routeTo)) {
            history.push(routeTo);
          }

          onClick?.();
        });
      }}
    >
      <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
        <Icon
          type={iconType}
          size="large"
          sx={{
            color: "inherit",
            flexShrink: 0,
          }}
        />
        {isDefined(badgeCount) && badgeCount > 0 && (
          <Box sx={{ position: "absolute", top: -4, right: -8 }}>
            <Badge count={badgeCount} size="small" max={99} />
          </Box>
        )}
      </Box>

      {isDefined(children) && (
        <Text
          variant="caption"
          sx={{
            color: "inherit",
            fontSize: "0.625rem",
            lineHeight: 1.2,
            textAlign: "center",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "block",
            width: "100%",
          }}
        >
          {children}
        </Text>
      )}
    </Box>
  );
}
